import { useQuery } from "@tanstack/react-query";
import { createContext, type ReactNode, useContext, useMemo } from "react";

import { api } from "@/api";
import { useSession } from "@/session/SessionProvider";

export type Capability =
  | "casino_betting"
  | "platform_betting"
  | "packs_ownership"
  | "buyback"
  | "prize_pools";

type CapabilityContextValue = {
  loading: boolean;
  can: (capability: Capability) => boolean;
  reason: (capability: Capability) => string | null;
};

const CapabilityContext = createContext<CapabilityContextValue | null>(null);

export function CapabilityProvider({ children }: { children: ReactNode }) {
  const { session } = useSession();
  const profile = useQuery({
    queryKey: ["player", session?.playerId],
    queryFn: api.player,
    enabled: Boolean(session),
    staleTime: 60_000,
  });

  const value = useMemo<CapabilityContextValue>(
    () => ({
      loading: Boolean(session) && profile.isPending,
      can: (capability) =>
        Boolean(session && profile.data?.capabilities[capability]),
      reason: (capability) => {
        if (!session) return "Sign in to use this feature.";
        if (profile.isPending) return "Checking regional availability…";
        if (!profile.data?.capabilities[capability]) {
          return `${capability.replaceAll("_", " ")} is unavailable in this region.`;
        }
        return null;
      },
    }),
    [session, profile.data, profile.isPending],
  );

  return (
    <CapabilityContext.Provider value={value}>
      {children}
    </CapabilityContext.Provider>
  );
}

export function useCapabilities() {
  const value = useContext(CapabilityContext);
  if (!value) {
    throw new Error("useCapabilities must be used inside CapabilityProvider");
  }
  return value;
}
